import useTranslation from 'next-translate/useTranslation'
import get from 'lodash/get'
import ReactTooltip from 'react-tooltip'
import { SelectType } from 'tennders-common/types/SelectType'
import React from 'react'
import { isEmpty, isNil } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import stylesInput from '@/styles/Input.module.scss'

type GroupType = {
  id?: string | number,
  items: SelectType[],
  label?: string
}

type ValueType = string | number | boolean

type ItemProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>

type Props = Omit<ItemProps, 'onChange' | 'translate'> & {
  form?: Record<string, any>,
  name?: string,
  onChange?: (name: string, value: ValueType | ValueType[]) => void,
  placeholder?: string,
  multiple?: boolean,
  multi?: boolean,
  nullValue?: any,
  onlyInput?: boolean
  withDiv?: boolean,
  onSet?: (value: ValueType | ValueType[]) => void,
  value?: string | number | null,
  forceValue?: string | number | null,
  error?: string,
  errors?: Record<string, any>,
  top?: boolean | string | number,
  labelClass?: string,
  children?: React.ReactNode | React.ReactNode[],
  eClass?: string,
  label?: string,
  inputClass?: string,
  labelKey?: string,
  url?: string,
  items?: SelectType[],
  translate?: boolean,
  style?: React.CSSProperties,
  forcedValue?: boolean,
  className?: string,
  disabled?: boolean,
  groups?: GroupType[],
  onBlur?: () => void,
  required?: boolean,
  emptyText ?: string,
  defaultItem?: SelectType
  allOption?: boolean,
}

const GSelect = ({
  form,
  name,
  onChange,
  onSet,
  multiple,
  onlyInput,
  multi,
  onBlur,
  nullValue,
  value,
  forceValue,
  error,
  errors,
  items,
  label,
  labelKey,
  eClass,
  className,
  translate,
  labelClass,
  forcedValue,
  groups,
  disabled,
  style = {},
  required,
  emptyText,
  defaultItem,
  allOption,
  ...rest
}: Props) => {
  const { t } = useTranslation()
  const choose = emptyText || t('common:select')
  const onFormChange = (e) => {
    if (!!multiple || !!multi) {
      const vals = []
      let opt
      for (let i = 0, len = e.target.options.length; i < len; i += 1) {
        opt = e.target.options[i]
        if (opt.selected) {
          vals.push(opt.value)
        } else if (vals.indexOf(opt.value) > -1) {
          vals.splice(vals.indexOf(opt.value), 1)
        }
      }

      if (onChange) {
        onChange(name, vals)
      }
    } else if (onChange) {
      onChange(name, e.target.value)
    } else if (onSet) {
      onSet(e.target.value)
    }
    if (onBlur) {
      onBlur()
    }
  }

  const inputVal = forceValue || (form ? form[name] : value)
  const inputError = errors ? errors[name] : error

  const getError = () => {
    if (errors && name) {
      return errors[name] && errors[name].includes('variables:') ? t(errors[name]) : errors[name]
    }
    return error
  }

  const renderInput = () => (
    <>
      <select
        name={name}
        id={name}
        style={{ minWidth: 100, ...style }}
        multiple={!!multiple || !!multi}
        disabled={!!disabled}
        onChange={onFormChange}
        data-tip={(rest && rest['data-for'])}
        data-color={(rest && rest['data-color'])}
        data-for={(rest && rest['data-for']) || ''}
        value={inputVal?.id ?? inputVal ?? ''}
        className={(className || 'form-select w-auto mr-4')
                    + (inputError ? ' error' : '') + (disabled ? ' disabled' : '')
                    + (!isNil(inputVal?.id ?? inputVal ?? '') && !isEmpty(inputVal?.id?.toString() ?? inputVal?.toString() ?? '') && inputVal !== 'false' ? ' selected' : '')}
      >
        {defaultItem && <option value={defaultItem.value}>{defaultItem.label}</option>}
        {
          groups && groups.map((group, key) => (
            <optgroup key={group.label} label={translate ? t(`variables:${group.label}`) : group.label}>
              {group.items?.map((item, key) => (
                <option
                  key={item.id}
                  selected={item.value === value}
                  value={item.value || item.id}
                >
                  {translate ? t(`variables:${item.label}`) : item.label}
                </option>
              ))}
            </optgroup>
          ))
        }
        {!multiple && !multi && !forcedValue && !groups
          ? <option value={nullValue || false}>{choose}</option> : null}
        {items && items.map((item, i) => (
          <option
            key={get(item, 'id') + get(item, 'label') + get(item, 'value')}
            selected={item.value === value}
            value={typeof item.value === 'undefined' ? item.id : item.value}
          >
            {translate ? t(`variables:${item.label}`) : item.label}
          </option>
        ))}
      </select>
      {getError() && getError() !== '' ? (
        <span className="d-block mt-10">
          <FontAwesomeIcon icon={faCircleExclamation} data-tip data-for={name} size="lg" className="color-red" />
          <ReactTooltip id={name} type="error">
            {getError()}
          </ReactTooltip>
        </span>
      ) : null}
    </>
  )

  const hasLabel = labelKey || label

  return (
    onlyInput
      ? renderInput()
      : (
        <div {...rest} className={eClass || 'mr-8'}>
          <>
            {hasLabel && (
              <label
                htmlFor={name}
                className={labelClass || 'form-label'}
              >
                {labelKey ? t(labelKey) : label}
                {required && <span className={stylesInput.required}>*</span>}
                {' '}
              </label>
            )}
            {renderInput()}
          </>
        </div>
      )
  )
}

export default GSelect
